<template>
  <div class="home-page">
    <header class="home-header">
      <div class="home-logo">
        <img src="@/assets/logo.png" alt="BISU RIE System logo" />
        <div class="home-logo-text">
          <span class="tag">Digital Platform</span>
          <span class="name">RIE System</span>
        </div>
      </div>
      <nav class="home-nav">
        <button class="nav-link primary" @click="goSignIn">Sign in</button>
        <button class="nav-link ghost" @click="goSignUp">Sign up</button>
      </nav>
    </header>

    <main class="home-hero">
      <section class="hero-text">
        <h1>
          Research, Innovation
          <span class="accent">&amp; Extension in one place.</span>
        </h1>
        <p>
          Simple portal for BISU faculty to record and monitor
          research, projects, and extension work.
        </p>
        <div class="hero-actions">
          <button class="cta" @click="goSignIn">Get started</button>
          <button class="cta-ghost" @click="goSignUp">Create faculty account</button>
        </div>
      </section>

      <section class="hero-stats">
        <div class="stat-card">
          <span class="stat-number">500+</span>
          <span class="stat-label">Research outputs</span>
        </div>
        <div class="stat-card">
          <span class="stat-number">150+</span>
          <span class="stat-label">Faculty users</span>
        </div>
        <div class="stat-card">
          <span class="stat-number">80+</span>
          <span class="stat-label">Extension programs</span>
        </div>
      </section>
    </main>

    <section class="home-about">
      <h2>About the System</h2>
      <p>
        The Research Faculty Management System is a web-based platform that helps institutions manage and monitor faculty research efficiently. It allows faculty to submit and update their research information, while administrators can review data, track productivity, and generate reports. Overall, it reduces manual work, improves data organization, and supports better decision-making.
      </p>
    </section>

    <section class="home-team">
      <div class="home-team__header">
        <h2>Our Team</h2>
        <select v-model="selectedPhase" class="home-team__select">
          <option value="phase1">Phase 1</option>
          <option value="phase2">Phase 2</option>
        </select>
      </div>

      <div class="home-team__members" v-if="selectedPhase === 'phase1'">
        <div class="home-team__card" v-for="(member, idx) in phase1Members" :key="member.name">
          <img v-if="member.photo" class="home-team__photo" :src="member.photo" :alt="member.name" />
          <div v-else class="home-team__placeholder">
            <img
              class="home-team__placeholder-icon"
              :src="idx % 2 === 0 ? boyIcon : girlIcon"
              :alt="idx % 2 === 0 ? 'Boy icon' : 'Girl icon'"
            />
          </div>
          <h3>{{ member.name }}</h3>
          <p>{{ member.role }}</p>
        </div>
      </div>

      <div class="home-team__members" v-else>
        <div class="home-team__card" v-for="(member, idx) in phase2Members" :key="member.name">
          <img v-if="member.photo" class="home-team__photo" :src="member.photo" :alt="member.name" />
          <div v-else class="home-team__placeholder">
            <img
              class="home-team__placeholder-icon"
              :src="idx % 2 === 0 ? boyIcon : girlIcon"
              :alt="idx % 2 === 0 ? 'Boy icon' : 'Girl icon'"
            />
          </div>
          <h3>{{ member.name }}</h3>
          <p>{{ member.role }}</p>
        </div>
      </div>
    </section>

    <section class="home-contact" aria-labelledby="contact-us-heading">
      <div class="home-contact__inner">
        <h2 id="contact-us-heading">Contact Us</h2>
        <ul class="home-contact__list">
          <li class="home-contact__item">
            <span class="home-contact__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.86.3 1.7.54 2.51a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.57-1.06a2 2 0 0 1 2.11-.45c.81.24 1.65.42 2.51.54A2 2 0 0 1 22 16.92Z"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <a class="home-contact__link" href="tel:09651463870">0965 146 3870</a>
          </li>

          <li class="home-contact__item">
            <span class="home-contact__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="m22 6-10 7L2 6"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <a class="home-contact__link" href="mailto:RieSystem@gmail.com">RieSystem@gmail.com</a>
          </li>
        </ul>
      </div>
    </section>

    <footer class="home-footer" aria-label="Site footer">
      <p class="home-footer__text">Crafted by the RIE System student team • BISU Bilar Campus</p>
      <div class="home-footer__logos" aria-label="Partner marks">
        <span class="home-footer__logo home-footer__logo--img home-footer__logo--rie" title="RIE System">
          <img src="@/assets/logo.png" alt="RIE System mark" />
        </span>
        <span class="home-footer__logo home-footer__logo--img home-footer__logo--bisu" title="BISU">
          <img src="@/assets/bisu-logo2.png" alt="BISU mark" />
        </span>

        <span class="home-footer__logo home-footer__logo--img home-footer__logo--ctech" title="CTECH">
          <img src="@/assets/CTECH_LOGO.jpg" alt="CTECH mark" />
        </span>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'HomePage',
  data() {
    return {
      selectedPhase: 'phase1',
      boyIcon: require('@/assets/Members/boy_icon.jpg'),
      girlIcon: require('@/assets/Members/girl_icon.png'),
      phase1Members: [
        { name: 'Engr. Max Angelo Perin', role: 'Advisor • BISU Bilar Computer Science Department Instructor', photo: require('@/assets/Members/Max_Angelo_Perin.jpg') },
        { name: 'Cecille Gumanoy', role: 'Advisor • BISU Bilar Computer Science Department Instructor', photo: require('@/assets/Members/Cecille_Gumanoy.jpg') },
        { name: 'Darrel Cardaña', role: 'Advisor • BISU Bilar Computer Science Department Instructor', photo: require('@/assets/Members/Darrel_Cardana.jpg') },
        { name: 'Sunshine Duran', role: 'Lead Developer • BISU Bilar Bachelor of Science in Computer Science', photo: require('@/assets/Members/Sunshine_Duran.jpg') },
        { name: 'Jomari Bonsayon', role: 'Assistant Developer • BISU Bilar Bachelor of Science in Computer Science' , photo: require('@/assets/Members/Jomari_Bonsayon.jpg') },
        { name: 'Lizell Virador', role: 'Front-End Developer • BISU Bilar Bachelor of Science in Computer Science', photo: require('@/assets/Members/Lizell_Virador.jpg') },
        { name: 'Eljen Adanza', role: 'Documentation Specialist • BISU Bilar Bachelor of Science in Computer Science', photo: require('@/assets/Members/Eljen_Adanza.jpg') },
        { name: 'HEART RAMIREZ', role: 'Backend Developer • BISU Bilar Bachelor of Science in Computer Science', photo: require('@/assets/Members/Heart_Ramirez.jpg') },
        { name: 'PHOEBE MAY LIMON', role: 'Backend Developer • BISU Bilar Bachelor of Science in Computer Science', photo: require('@/assets/Members/PHOEBE_MAY_LIMON.jpg') },
        { name: 'Geno Vasquez', role: 'Documentation Specialist • BISU Bilar Bachelor of Science in Computer Science', photo: require('@/assets/Members/Geno_Vasquez.jpg') }
      ],
      phase2Members: [
        { name: 'Engr. Max Angelo Perin', role: 'Advisor • BISU Bilar Computer Science Department Instructor', photo: require('@/assets/Members/Max_Angelo_Perin.jpg') },
        { name: 'Cecille Gumanoy', role: 'Advisor • BISU Bilar Computer Science Department Instructor', photo: require('@/assets/Members/Cecille_Gumanoy.jpg') },
        { name: 'Darrel Cardaña', role: 'Advisor • BISU Bilar Computer Science Department Instructor', photo: require('@/assets/Members/Darrel_Cardana.jpg') },
        { name: 'Sunshine Duran', role: 'Lead Developer • BISU Bilar Bachelor of Science in Computer Science' , photo: require('@/assets/Members/Sunshine_Duran.jpg') },
        { name: 'Jomari Bonsayon', role: 'Backend Developer • BISU Bilar Bachelor of Science in Computer Science' , photo: require('@/assets/Members/Jomari_Bonsayon.jpg') },
        { name: 'Ian Jake R. Gamana', role: 'Documentation Specialist • BISU Bilar Bachelor of Science in Computer Science', photo: require('@/assets/Members/IanJake_Gamana.jpg') },
        { name: 'Honey Mae D. Cahate', role: 'Backend Developer • BISU Bilar Bachelor of Science in Computer Science', photo: require('@/assets/Members/Honey_Mae_D_Cahate.jpg') },
        { name: 'Jesila G. Valentos', role: 'Backend Developer • BISU Bilar Bachelor of Science in Computer Science', photo: require('@/assets/Members/Jesila_Valentos.jpg') },
        { name: 'Ma. Lyza P. Quirol', role: 'Documentation Specialist • BISU Bilar Bachelor of Science in Computer Science', photo: require('@/assets/Members/Ma_Lyza_Quirol.jpg') },
        { name: 'Riza Jane C. Ilaga', role: 'Backend Developer • BISU Bilar Bachelor of Science in Computer Science', photo: require('@/assets/Members/Riza_Jane_C_Ilaga.jpg') }
      ]
    };
  },
  methods: {
    goSignIn() {
      this.$router.push({ name: 'SignIn' });
    },
    goSignUp() {
      this.$router.push({ name: 'SignUp' });
    }
  }
};
</script>

<style src="@/assets/styles/home.css"></style>
